import React from 'react';
import { FormattedMessage, FormattedDate } from '../../util/reactIntl';
import {
  DATE_TYPE_DATE,
  DATE_TYPE_TIME,
  DATE_TYPE_DATETIME,
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_NIGHT,
  LINE_ITEM_BASE_DOGS_IN_OFFICE_STANDARD,
  LINE_ITEM_BASE_DOGS_STANDARD,
  LINE_ITEM_BASE_DOGS_IN_OFFICE_HOLIDAY,
  LINE_ITEM_BASE_DOGS_HOLIDAY,
  LINE_ITEM_BASE_PUPPIES_IN_OFFICE_STANDARD,
  LINE_ITEM_BASE_PUPPIES_STANDARD,
  LINE_ITEM_BASE_PUPPIES_IN_OFFICE_HOLIDAY,
  LINE_ITEM_BASE_PUPPIES_HOLIDAY,
  LINE_ITEM_BASE_CATS_IN_OFFICE_STANDARD,
  LINE_ITEM_BASE_CATS_STANDARD,
  LINE_ITEM_BASE_CATS_IN_OFFICE_HOLIDAY,
  LINE_ITEM_BASE_CATS_HOLIDAY,
  propTypes,
} from '../../util/types';
import { subtractTime, isDST } from '../../util/dates';

import css from './OrderBreakdown.module.css';

const DSTMaybe = props => {
  const { startDate, endDate, isStart, dateType, timeZone } = props;
  const isDayTimeRange = dateType === DATE_TYPE_DATETIME;
  if (!isDayTimeRange) {
    return null;
  }

  const isStartInDST = isDST(startDate, timeZone);
  const isEndInDST = isDST(endDate, timeZone);
  const isDSTChanged = isStartInDST !== isEndInDST;
  const showDSTMsgForStart = isDSTChanged && isStart && isStartInDST;
  const showDSTMsgForEnd = isDSTChanged && !isStart && isEndInDST;

  return showDSTMsgForStart || showDSTMsgForEnd ? (
    <div className={css.itemLabel}>
      <FormattedMessage id="OrderBreakdown.bookingWithDSTInEffect" />
    </div>
  ) : null;
};

const BookingPeriod = props => {
  const { startDate, endDate, dateType, timeZone, showTime } = props;
  const timeZoneMaybe = timeZone ? { timeZone } : null;

  const timeFormatOptions =
    dateType === DATE_TYPE_DATE && !showTime
      ? {
          weekday: 'long',
        }
      : {
          weekday: 'short',
          hour: 'numeric',
          minute: 'numeric',
        };

  const dateFormatOptions = {
    month: 'short',
    day: 'numeric',
  };

  return (
    <>
      <div className={css.bookingPeriod}>
        <div className={css.bookingPeriodSectionLeft}>
          <div className={css.dayLabel}>
            <FormattedMessage id="OrderBreakdown.bookingStart" />
          </div>
          <div className={css.dayInfo}>
            <FormattedDate value={startDate} {...timeFormatOptions} {...timeZoneMaybe} />
          </div>
          <div className={css.itemLabel}>
            <FormattedDate value={startDate} {...dateFormatOptions} {...timeZoneMaybe} />
          </div>
          <DSTMaybe
            startDate={startDate}
            endDate={endDate}
            isStart={true}
            dateType={dateType}
            timeZone={timeZone}
          />
        </div>

        <div className={css.bookingPeriodSectionRight}>
          <div className={css.dayLabel}>
            <FormattedMessage id="OrderBreakdown.bookingEnd" />
          </div>
          <div className={css.dayInfo}>
            <FormattedDate value={endDate} {...timeFormatOptions} {...timeZoneMaybe} />
          </div>
          <div className={css.itemLabel}>
            <FormattedDate value={endDate} {...dateFormatOptions} {...timeZoneMaybe} />
          </div>
          <DSTMaybe
            startDate={startDate}
            endDate={endDate}
            isStart={false}
            dateType={dateType}
            timeZone={timeZone}
          />
        </div>
      </div>
    </>
  );
};

const setDateWithTime = (date, timeString) => {
    if (!date || !timeString) return date;
    const newDate = new Date(date);
    const [hour, minute] = timeString.split(':').map(s => parseInt(s, 10));
    newDate.setHours(hour);
    newDate.setMinutes(minute);
    return newDate;
};

/**
 * A line-item to show booking period for the OrderBreakdown
 *
 * @component
 * @param {Object} props
 * @param {propTypes.booking?} props.booking
 * @param {LINE_ITEM_NIGHT | LINE_ITEM_DAY | LINE_ITEM_HOUR} props.code
 * @param {DATE_TYPE_DATE | DATE_TYPE_TIME | DATE_TYPE_DATETIME} props.dateType
 * @param {string} props.timeZone IANA time zone name
 * @returns {JSX.Element} line-item element for the order breakdown
 */
const LineItemBookingPeriod = props => {
  const { booking, transaction, code, dateType, timeZone } = props;

  if (!booking) {
    return null;
  }
  // Attributes: displayStart and displayEnd can be used to differentiate shown time range
  // from actual start and end times used for availability reservation. It can help in situations
  // where there are preparation time needed between bookings.
  // Read more: https://www.sharetribe.com/api-reference/marketplace.html#bookings
  const { start, end, displayStart, displayEnd } = booking.attributes;
  const localStartDate = displayStart || start;
  const localEndDateRaw = displayEnd || end;

  const showInclusiveEndDate = [
    LINE_ITEM_DAY,
    LINE_ITEM_BASE_DOGS_IN_OFFICE_STANDARD,
    LINE_ITEM_BASE_DOGS_STANDARD,
    LINE_ITEM_BASE_DOGS_IN_OFFICE_HOLIDAY,
    LINE_ITEM_BASE_DOGS_HOLIDAY,
    LINE_ITEM_BASE_PUPPIES_IN_OFFICE_STANDARD,
    LINE_ITEM_BASE_PUPPIES_STANDARD,
    LINE_ITEM_BASE_PUPPIES_IN_OFFICE_HOLIDAY,
    LINE_ITEM_BASE_PUPPIES_HOLIDAY,
    LINE_ITEM_BASE_CATS_IN_OFFICE_STANDARD,
    LINE_ITEM_BASE_CATS_STANDARD,
    LINE_ITEM_BASE_CATS_IN_OFFICE_HOLIDAY,
    LINE_ITEM_BASE_CATS_HOLIDAY,
  ].includes(code);
  const endDay = showInclusiveEndDate ? subtractTime(localEndDateRaw, 1, 'days') : localEndDateRaw;

  const protectedData = transaction?.attributes?.protectedData || {};
  const { startTime, endTime } = protectedData;

  const startDate = setDateWithTime(localStartDate, startTime);
  const endDate = setDateWithTime(endDay, endTime);

  const shouldShowTime = !!(startTime || endTime);

  return (
    <>
      <div className={css.lineItem}>
        <BookingPeriod
          startDate={startDate}
          endDate={endDate}
          dateType={dateType}
          timeZone={timeZone}
          showTime={shouldShowTime}
        />
      </div>
      <hr className={css.totalDivider} />
    </>
  );
};

export default LineItemBookingPeriod;
